import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  Button, 
  useTheme, 
  useMediaQuery, 
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Icons
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import ComputerIcon from '@mui/icons-material/Computer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import VideocamIcon from '@mui/icons-material/Videocam';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FactCheckIcon from '@mui/icons-material/FactCheck';

const TrainingServices: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Training areas from the website
  const trainingAreas = [
    "Block Chain Development", "CISSP", "IOT", "Cyber Security", "CCNA", 
    "Artificial Intelligence", "Robotics", "BIG DATA Analytics", "Agile (scrum)", 
    "Software Quality Assurance (SQA)", "Mean Stack Development", "Python & Perl", 
    "Six Sigma (Green Belt & Black Belt)", "Software Architecture", "Freelance Training", 
    "Call Center Training", "SQA", "Digital Marketing", "Cloud Computing", "E-Commerce", 
    "Free Lancing", "Project Management", "Amazon Web Services (AWS)", "CITRIX", 
    "COMPTIA", "EC-Council", "ISACA", "Linux", "SAP", "Python", "VMWARE"
  ];

  // Business advantages from the website
  const businessAdvantages = [
    "Process efficiency, flexibility, & productivity",
    "Problem-solving skills",
    "Innovation in strategies and products",
    "Job satisfaction & morale",
    "Employee motivation & retention",
    "Customer satisfaction"
  ];

  // Training approaches
  const trainingApproaches = [
    {
      title: "ONLINE",
      description: "Our online training courses give you the flexibility to enroll in our industry-leading courses wherever you have access to the internet.",
      icon: <LanguageIcon fontSize="large" />,
      details: [
        "Live synchronous instruction from any location",
        "Extensive Guaranteed-to-Run course schedule",
        "Industry-recognized training with authorized courseware",
        "Top-tier instructors with practical experience",
        "Labs on Demand for hands-on practice"
      ]
    },
    {
      title: "ON DEMAND",
      description: "Everyone has their own learning style. Our on-demand training lets you learn at your own pace with award-winning courseware.",
      icon: <AccessTimeIcon fontSize="large" />,
      details: [
        "Learn at your own pace and schedule",
        "Self-paced training with full access to training materials",
        "Courseware developed for professional certifications",
        "Practice exams and lab environments",
        "24/7 access to learning resources"
      ]
    },
    {
      title: "MENTORED LEARNING",
      description: "Control the pace of the course and immediately apply what you've learned with personalized mentoring.",
      icon: <PeopleAltIcon fontSize="large" />,
      details: [
        "Flexible training schedules",
        "Control the pace of the course",
        "Immediately apply what you've learned",
        "Personalized mentoring to ask questions",
        "Build around meetings and deadlines"
      ]
    },
    {
      title: "IN CLASS",
      description: "Our classroom environment creates an enriching, dynamic, and valuable hands-on learning experience.",
      icon: <SchoolIcon fontSize="large" />,
      details: [
        "Lectures and demonstrations by industry experts",
        "Hands-on practice in real environments",
        "Valuable student and peer interaction",
        "Online resources to use during and after class",
        "Learning at the right time, in the right location"
      ]
    },
    {
      title: "CUSTOM",
      description: "Build private training around your organization's unique learning needs with customized courses and materials.",
      icon: <SettingsIcon fontSize="large" />,
      details: [
        "Customized courses for your organization",
        "Train as many or as few employees as needed",
        "Delivered at your location or ours",
        "Scheduled around your organization's needs",
        "Team-building experience that enhances success"
      ]
    }
  ];

  // Integrated learning approach
  const learningApproach = [
    {
      title: "ASSESS",
      description: "The assess portion helps identify why training is needed and at what level, maximizing your investment.",
      icon: <AssessmentIcon fontSize="large" color="primary" />
    },
    {
      title: "LEARN",
      description: "Choose from various learning methods that fit individual learning styles and business training needs.",
      icon: <SchoolIcon fontSize="large" color="primary" />
    },
    {
      title: "REINFORCE",
      description: "Tools to help retain course material, including hands-on labs, reference materials, and practice exams.",
      icon: <FactCheckIcon fontSize="large" color="primary" />
    },
    {
      title: "SUPPORT",
      description: "Ongoing support including consultations, learning management system, and technical help desk.",
      icon: <SupportAgentIcon fontSize="large" color="primary" />
    },
    {
      title: "VALIDATE",
      description: "Identify learning that occurred, areas to improve, and value of skills acquired through evaluations and assessments.",
      icon: <VerifiedIcon fontSize="large" color="primary" />
    }
  ];

  // Delivery formats
  const deliveryFormats = [
    {
      title: "Classroom",
      description: "Interact face-to-face with subject matter experts on state-of-the-art equipment in a ready-to-learn environment.",
      icon: <DesktopWindowsIcon fontSize="large" color="primary" />
    },
    {
      title: "Virtual Classroom",
      description: "Experience live instructor-led online training with the flexibility of cross-platform support.",
      icon: <VideocamIcon fontSize="large" color="primary" />
    },
    {
      title: "Private Company Training",
      description: "Train your team at a site of your choice with customized curriculums and materials.",
      icon: <BusinessIcon fontSize="large" color="primary" />
    },
    {
      title: "e-Learning",
      description: "Convenient 24/7 access to instructor-led videos and hands-on activities that fit your schedule.",
      icon: <LaptopMacIcon fontSize="large" color="primary" />
    },
    {
      title: "Blended Live",
      description: "Combines the structure of instructor-led training with the flexibility of on-demand learning.",
      icon: <PersonalVideoIcon fontSize="large" color="primary" />
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Training Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Innovative, flexible learning methods designed for your success
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          transform: 'translate(30%, 30%)',
          zIndex: 1
        }} />
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Business Solutions Section */}
        <Box component={motion.div} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          sx={{ mb: 8 }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center" color="#0c2340">
            Business Solutions
          </Typography>
          <Typography variant="h6" gutterBottom textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            Custom training solutions to meet your specific business needs
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
              <Paper 
                component={motion.div} 
                whileHover={{ y: -10 }} 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '5px',
                  background: 'linear-gradient(90deg, #1976d2, #2196f3)'
                }} />
                <Box display="flex" alignItems="center" mb={2}>
                  <BusinessIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight="bold">
                    Enterprise Training Solutions
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  Customized training programs designed for your organization's unique needs. Our enterprise solutions are scalable, targeted, and result-oriented.
                </Typography>
                <List>
                  <ListItem sx={{ p: 0, mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Custom curriculum development" />
                  </ListItem>
                  <ListItem sx={{ p: 0, mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Multi-format delivery options" />
                  </ListItem>
                  <ListItem sx={{ p: 0, mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Progress tracking and reporting" />
                  </ListItem>
                </List>
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
              <Paper 
                component={motion.div} 
                whileHover={{ y: -10 }} 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '5px',
                  background: 'linear-gradient(90deg, #1976d2, #2196f3)'
                }} />
                <Box display="flex" alignItems="center" mb={2}>
                  <GroupsIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight="bold">
                    Team Skills Development
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  Elevate your team's capabilities with our specialized skills development programs. Focus on collaborative learning and practical application.
                </Typography>
                <List>
                  <ListItem sx={{ p: 0, mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Role-specific training modules" />
                  </ListItem>
                  <ListItem sx={{ p: 0, mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Team-building exercises" />
                  </ListItem>
                  <ListItem sx={{ p: 0, mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Real-world project simulations" />
                  </ListItem>
                </List>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Advantages to IT Training */}
        <Box component={motion.div} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          sx={{ mb: 8 }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center" color="#0c2340">
            Advantages to IT Training for Your Employees
          </Typography>
          <Typography variant="h6" gutterBottom textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            Investing in your team's skills yields multiple returns for your business
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {businessAdvantages.map((advantage, index) => (
              <Box 
                key={index}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' }
                }}
              >
                <Paper 
                  component={motion.div} 
                  whileHover={{ scale: 1.03 }}
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center',
                    borderLeft: 4, 
                    borderColor: 'primary.main',
                    borderRadius: '8px',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.05)'
                  }}
                >
                  <AutoGraphIcon color="primary" sx={{ fontSize: 30, mr: 2 }} />
                  <Typography variant="body1" fontWeight="medium">
                    {advantage}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Training Categories */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          sx={{ mb: 8 }}
        >
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ textAlign: 'center', mb: 2 }} color="#0c2340">
            Training
          </Typography>
          <Divider sx={{ width: '80px', borderWidth: 2, borderColor: '#1976d2', my: 3, mx: 'auto' }} />
          <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 3, fontSize: '1.1rem', maxWidth: '900px', mx: 'auto' }}>
            As changes in technology have accelerated, it's become even more essential for people to master technology to be productive, 
            invaluable employees who optimize, program, and invent solutions and even grow companies of their own.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 5, fontSize: '1.1rem', maxWidth: '900px', mx: 'auto' }}>
            We do not believe in typical class room style training and instead have specially designed courses based on group based 
            interactive exercises, quizzes and games. We believe in true transfer of technology where we impart our practical knowledge 
            to our customers to give them the maximum know how of the industrial trends and needs.
          </Typography>
          
          {/* Training Areas */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 4, textAlign: 'center' }} color="#0c2340">
              We provide training in the following areas:
            </Typography>
            <Box 
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 2
              }}
            >
              {trainingAreas.map((area, index) => (
                <Chip
                  key={index}
                  label={area}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    borderRadius: '8px',
                    px: 1,
                    py: 2.5,
                    fontSize: '0.9rem'
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Learn In Your Own Way Section */}
        <Box 
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          sx={{ mb: 8 }}
        >
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3, textAlign: 'center' }} color="#0c2340">
            Learn in your own way
          </Typography>
          <Divider sx={{ width: '80px', borderWidth: 2, borderColor: '#1976d2', my: 3, mx: 'auto' }} />
          <Typography paragraph sx={{ fontSize: '1.1rem' }}>
            All of Right Track Technologies Limited's Learning Methods deliver the same comprehensive content—in online and traditional classroom settings,
            virtually, or through a hybrid approach. So you can choose the option that best fits your organization's
            and employees' needs.
          </Typography>
          
          <Box sx={{ 
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e4e4 100%)', 
            p: 4, 
            borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
          }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ textAlign: 'center', color: '#0c2340' }}>
              Continuously upgrade the people who drive your business
            </Typography>
            <Typography paragraph>
              Whether you're a startup, a small or
              mid-sized or a global enterprise, Right Track Technologies Limited training yields a 4:1 benefit-to-cost ratio, based on manager surveys of the
              improved performance of their teams after training.
            </Typography>
          </Box>
        </Box>

        {/* Our Approach Section - Tab-Based */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ textAlign: 'center', mb: 2 }} color="#0c2340">
            Our Approach
          </Typography>
          <Divider sx={{ width: '80px', borderWidth: 2, borderColor: '#1976d2', my: 3, mx: 'auto' }} />
          
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="training approaches tabs"
            sx={{
              mb: 4,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontWeight: 'bold',
                fontSize: { xs: '0.875rem', md: '1rem' }
              },
              '& .Mui-selected': {
                color: '#1976d2'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            {trainingApproaches.map((approach, index) => (
              <Tab 
                key={index} 
                label={approach.title} 
                icon={approach.icon} 
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
            ))}
          </Tabs>
          
          {trainingApproaches.map((approach, index) => (
            <Box
              key={index}
              role="tabpanel"
              hidden={tabValue !== index}
              id={`approach-tabpanel-${index}`}
              aria-labelledby={`approach-tab-${index}`}
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: tabValue === index ? 1 : 0 }}
              transition={{ duration: 0.5 }}
            >
              {tabValue === index && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                    <Typography variant="h4" fontWeight="bold" gutterBottom color="#0c2340">
                      {approach.title}
                    </Typography>
                    <Divider sx={{ width: '60px', borderWidth: 2, borderColor: '#1976d2', my: 3 }} />
                    <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                      {approach.description}
                    </Typography>
                    
                    <List>
                      {approach.details.map((detail, i) => (
                        <ListItem key={i} sx={{ p: 0, mb: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={detail} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                    <Box
                      component="img"
                      src={`/images/training-${approach.title.toLowerCase().replace(' ', '-')}.jpg`}
                      alt={`${approach.title} Training`}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '16px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                        display: { xs: 'none', md: 'block' }
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* Delivery Formats */}
        <Box component={motion.div} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          sx={{ mb: 8 }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center" color="#0c2340">
            Training Delivery Formats
          </Typography>
          <Divider sx={{ width: '80px', borderWidth: 2, borderColor: '#1976d2', my: 3, mx: 'auto' }} />
          <Typography variant="h6" gutterBottom textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            Flexible learning options to fit your schedule and preference
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {deliveryFormats.map((format, index) => (
              <Box 
                key={index}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(25% - 18px)' }
                }}
              >
                <Paper 
                  component={motion.div} 
                  whileHover={{ y: -10 }}
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                    overflow: 'hidden'
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 2 }}>
                      {format.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold" color="#0c2340">
                      {format.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format.description}
                    </Typography>
                  </CardContent>
                </Paper>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Integrated Learning Approach */}
        <Box component={motion.div} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          sx={{ mb: 8 }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center" color="#0c2340">
            Our Integrated Learning Approach
          </Typography>
          <Divider sx={{ width: '80px', borderWidth: 2, borderColor: '#1976d2', my: 3, mx: 'auto' }} />
          <Typography variant="h6" gutterBottom textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            A comprehensive methodology designed for maximum knowledge retention and application
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 3 }}>
            {learningApproach.map((step, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  p: 3,
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {step.icon}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom align="center" color="#0c2340">
                  {step.title}
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary">
                  {step.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Call to Action */}
        <Box component={motion.div} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          sx={{ textAlign: 'center', mb: 4 }}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 4, md: 6 }, 
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
              color: 'white',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Decorative Elements */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '200px',
              height: '200px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
              borderRadius: '50%',
              transform: 'translate(-30%, -30%)',
              zIndex: 1
            }} />
            
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
              Ready to Advance Your Team's Skills?
            </Typography>
            <Typography variant="h6" paragraph sx={{ mb: 4, maxWidth: 800, mx: 'auto', opacity: 0.9 }}>
              Our training programs are designed to equip your team with the latest skills and knowledge needed to excel in today's competitive landscape.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Button 
                component={Link} 
                to="/contact" 
                variant="contained" 
                size="large"
                endIcon={<ArrowRightAltIcon />}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: '50px',
                  background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Request Training
              </Button>
              <Button 
                component={Link} 
                to="/services" 
                variant="outlined"
                size="large"
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: '50px',
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Explore All Services
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default TrainingServices; 